import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  getHcpRequestDraft,
  putHcpRequestDraft,
  postValidateHcpDraftSubmission,
  postSubmitHcpRequestDraft,
} from './hcpRequestDraftApi';
import { mergeRequestDraftFieldPolicy } from './requestDraftFieldPolicy';
import {
  clonePopulated,
  buildApplicantBaseline,
  buildLicenseBaseline,
  buildExperienceBaseline,
} from './draftFormUtils';
import ApplicantSection from './components/ApplicantSection';
import LicenseSection from './components/LicenseSection';
import QualificationsSection from './components/QualificationsSection';
import SubmissionReadinessAlert from './components/SubmissionReadinessAlert';
import styles from './hcpRequestDraft.module.css';

const SAVE_DEBOUNCE_MS = 700;

/**
 * @typedef {{
 *   applicant: object,
 *   license: object,
 *   experience: object,
 *   draftId: number,
 *   revision: string,
 * }} HighlightBaseline
 */

/**
 * Prefilled draft form: survives refresh via persisted `/api/requests/drafts/:id`.
 */
export default function HcpRequestDraftPage() {
  const { draftId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [highlightBaseline, setHighlightBaseline] = useState(null);
  const [draftMeta, setDraftMeta] = useState([]);

  const [serverRow, setServerRow] = useState(null);
  const [populated, setPopulated] = useState(() => clonePopulated({}));
  const populatedRef = useRef(populated);

  const [saveState, setSaveState] = useState('idle');
  const saveTimerRef = useRef(null);
  const lastSavedHashRef = useRef('');

  const [submissionGate, setSubmissionGate] = useState({
    checking: false,
    checked: false,
    valid: true,
    errors: [],
    blockingMessage: null,
  });

  const [submitCheckRunning, setSubmitCheckRunning] = useState(false);
  const [submitRunning, setSubmitRunning] = useState(false);
  const [submitViolation, setSubmitViolation] = useState(null);
  const [updateProfileAfterSubmit, setUpdateProfileAfterSubmit] =
    useState(false);

  const isDraftEditable = useMemo(
    () => String(serverRow?.status ?? 'draft').toLowerCase() === 'draft',
    [serverRow?.status]
  );

  const isDraftEditableRef = useRef(isDraftEditable);
  useEffect(() => {
    isDraftEditableRef.current = isDraftEditable;
  }, [isDraftEditable]);

  const updateProfileAllowed = Boolean(
    serverRow?.features?.updateProfileOnSubmit
  );

  const policy = useMemo(
    () =>
      mergeRequestDraftFieldPolicy(serverRow?.populated?.meta?.fieldEditPolicy),
    [serverRow]
  );

  const syncPopulatedHash = () =>
    JSON.stringify({
      applicantInfo: populatedRef.current.applicantInfo,
      licenseInfo: populatedRef.current.licenseInfo,
      qualifications: populatedRef.current.qualifications,
    });

  useEffect(() => {
    populatedRef.current = populated;
  }, [populated]);

  const refreshReadiness = useCallback(
    async (useClientPayload, { silent = false } = {}) => {
      const idNum = Number(draftId);
      if (!Number.isFinite(idNum)) return null;
      if (!silent) {
        setSubmissionGate((s) => ({
          ...s,
          checking: true,
          checked: false,
        }));
      }
      try {
        const opts = useClientPayload
          ? { populated: clonePopulated(populatedRef.current) }
          : {};
        const res = await postValidateHcpDraftSubmission(String(idNum), opts);
        const data = res?.data;
        setSubmissionGate({
          checking: false,
          checked: Boolean(res?.success && data),
          valid: Boolean(data?.valid),
          errors: Array.isArray(data?.errors) ? data.errors : [],
          blockingMessage: data?.blockingMessage || data?.message || null,
        });
        return data;
      } catch (e) {
        setSubmissionGate({
          checking: false,
          checked: true,
          valid: false,
          errors: [
            {
              code: 'VALIDATION_FAILED',
              detail:
                e?.response?.data?.message ||
                'Could not verify profile completeness.',
            },
          ],
          blockingMessage: 'Please complete your profile',
        });
        return null;
      }
    },
    [draftId]
  );

  const hydrateBaseline = useCallback((row) => {
    if (!row?.id) return;
    const revision = String(row.updatedAt ?? '');
    setHighlightBaseline((prev) => {
      if (prev && prev.draftId === row.id && prev.revision === revision) {
        return prev;
      }
      const pop = row.populated || {};
      return {
        draftId: row.id,
        revision,
        applicant: buildApplicantBaseline(row.initialSnapshot, pop),
        license: buildLicenseBaseline(row.initialSnapshot, pop),
        experience: buildExperienceBaseline(row.initialSnapshot, pop),
      };
    });
  }, []);

  const loadDraft = useCallback(async () => {
    const idNum = Number(draftId);
    if (!draftId || !Number.isFinite(idNum)) {
      setLoadError('Invalid draft identifier.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      setSubmitViolation(null);
      setUpdateProfileAfterSubmit(false);
      const res = await getHcpRequestDraft(String(idNum));
      if (!res?.success || !res.data) {
        setLoadError(res?.message || 'Could not load draft.');
        setLoading(false);
        return;
      }
      const data = res.data;
      setServerRow(data);
      setDraftMeta([
        ['Draft #', data.id],
        ['Status', data.status],
        ['Captured at', data.profileCapturedAt],
        ['Updated', data.updatedAt || '—'],
        ['Submitted at', data.submittedAt || '—'],
        ...(data.snapshotMeta?.liveProfileRefreshAppliedOnLoad
          ? [['Live profile applied', 'Yes — this load refreshed from profile']]
          : []),
        [
          'License selection',
          data.populated?.meta?.licenseSelectionReason || '—',
        ],
      ]);
      hydrateBaseline(data);
      const nextPop = clonePopulated(data.populated || {});
      setPopulated(nextPop);
      populatedRef.current = nextPop;
      lastSavedHashRef.current = JSON.stringify({
        applicantInfo: nextPop.applicantInfo,
        licenseInfo: nextPop.licenseInfo,
        qualifications: nextPop.qualifications,
      });
      await refreshReadiness(false, { silent: true });
      setLoading(false);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Draft not found or unavailable.';
      setLoadError(msg);
      setLoading(false);
    }
  }, [draftId, hydrateBaseline, refreshReadiness]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const flushSave = useCallback(async () => {
    const idNum = Number(draftId);
    if (!Number.isFinite(idNum)) return;
    if (!isDraftEditableRef.current) {
      setSaveState('idle');
      return;
    }
    const snapshot = syncPopulatedHash();
    if (snapshot === lastSavedHashRef.current) {
      setSaveState('idle');
      return;
    }
    setSaveState('saving');
    try {
      const putRes = await putHcpRequestDraft(String(idNum), {
        populated: populatedRef.current,
      });
      if (putRes?.success && putRes.data) {
        const d = putRes.data;
        setServerRow((prev) => ({
          ...(prev || {}),
          ...d,
          features: prev?.features ?? d.features,
          snapshotMeta: prev?.snapshotMeta ?? d.snapshotMeta,
        }));
        const nextPop = clonePopulated(d.populated || {});
        setPopulated(nextPop);
        populatedRef.current = nextPop;
        hydrateBaseline({
          id: d.id,
          updatedAt: d.updatedAt,
          populated: nextPop,
          initialSnapshot: d.initialSnapshot,
        });
      }
      lastSavedHashRef.current = JSON.stringify({
        applicantInfo: populatedRef.current.applicantInfo,
        licenseInfo: populatedRef.current.licenseInfo,
        qualifications: populatedRef.current.qualifications,
      });
      setSaveState('saved');
      await refreshReadiness(false, { silent: true });
      window.setTimeout(() => {
        setSaveState((s) => (s === 'saved' ? 'idle' : s));
      }, 2200);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Save failed';
      toast.error(msg);
      setSaveState('error');
    }
  }, [draftId, hydrateBaseline, refreshReadiness]);

  const flushSaveRef = useRef(flushSave);
  flushSaveRef.current = flushSave;

  useEffect(() => {
    const flushOnLeave = () => {
      flushSaveRef.current?.();
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        flushOnLeave();
      }
    };
    window.addEventListener('pagehide', flushOnLeave);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', flushOnLeave);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      const cur = populatedRef.current;
      const hash = JSON.stringify({
        applicantInfo: cur.applicantInfo,
        licenseInfo: cur.licenseInfo,
        qualifications: cur.qualifications,
      });
      if (hash !== lastSavedHashRef.current && isDraftEditableRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const queueSave = useCallback(() => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
      flushSaveRef.current?.();
    },
    []
  );

  const patchApplicant = useCallback(
    (key, value) => {
      if (!isDraftEditableRef.current) return;
      setPopulated((prev) => ({
        ...prev,
        applicantInfo: { ...prev.applicantInfo, [key]: value },
      }));
      queueSave();
    },
    [queueSave]
  );

  const patchLicense = useCallback(
    (key, value) => {
      if (!isDraftEditableRef.current) return;
      setPopulated((prev) => ({
        ...prev,
        licenseInfo: { ...prev.licenseInfo, [key]: value },
      }));
      queueSave();
    },
    [queueSave]
  );

  const patchExperience = useCallback(
    (key, value) => {
      if (!isDraftEditableRef.current) return;
      setPopulated((prev) => {
        const prevEx = prev.qualifications?.experience || {};
        return {
          ...prev,
          qualifications: {
            ...prev.qualifications,
            experience: {
              ...prevEx,
              [key]: value,
            },
          },
        };
      });
      queueSave();
    },
    [queueSave]
  );

  const handleLiveToggle = async (checked) => {
    if (!isDraftEditableRef.current) {
      toast.error('Submitted applications cannot be changed.');
      return;
    }
    const idNum = Number(draftId);
    if (!Number.isFinite(idNum)) return;
    try {
      await putHcpRequestDraft(String(idNum), {
        liveProfileRefresh: Boolean(checked),
      });
      await loadDraft();
      toast.success(
        checked
          ? 'Draft will refresh from your profile each time it is opened.'
          : 'Draft will keep stored snapshot unless you edit it.'
      );
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Update failed';
      toast.error(msg);
    }
  };

  const handleVerifyForSubmission = async () => {
    if (!isDraftEditableRef.current) {
      toast.error('This draft has already been submitted.');
      return;
    }
    setSubmitCheckRunning(true);
    try {
      await flushSave();
      const data = await refreshReadiness(true, { silent: false });
      if (!data?.valid) {
        toast.error(
          data?.blockingMessage ||
            data?.message ||
            'Please complete your profile'
        );
        return;
      }
      toast.success('Mandatory profile data is complete.');
    } finally {
      setSubmitCheckRunning(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!isDraftEditableRef.current) {
      toast.error('This draft has already been submitted.');
      return;
    }
    setSubmitViolation(null);
    setSubmitRunning(true);
    try {
      await flushSave();
      const readiness = await refreshReadiness(true, { silent: false });
      if (!readiness?.valid) {
        toast.error(
          readiness?.blockingMessage ||
            readiness?.message ||
            'Please complete your profile'
        );
        return;
      }
      const wantProfileUpdate =
        updateProfileAllowed && updateProfileAfterSubmit;
      const idNum = Number(draftId);
      const res = await postSubmitHcpRequestDraft({
        draft_id: idNum,
        populated: clonePopulated(populatedRef.current),
        request_type: populatedRef.current?.meta?.requestType,
        update_profile: wantProfileUpdate,
      });
      if (!res?.success) {
        if (res?.data?.formErrors?.length || res?.data?.profileErrors?.length) {
          setSubmitViolation({
            message: res.message || 'Could not submit',
            formErrors: res.data.formErrors || [],
            profileErrors: res.data.profileErrors || [],
          });
        }
        toast.error(res?.message || 'Submission failed');
        return;
      }
      toast.success('Application submitted successfully.');
      const ps = res?.data?.profileSync;
      if (wantProfileUpdate && ps && ps.success !== false && !ps.skipped) {
        if (ps.generalUpdated || ps.experienceUpdated) {
          toast.info(
            'Your HCP profile was updated with the editable fields from this submission (audit trail recorded).'
          );
        }
      } else if (wantProfileUpdate && ps && ps.success === false) {
        toast.warning(
          ps.generalError ||
            ps.experienceError ||
            ps.error ||
            'Application was submitted, but your profile could not be updated.'
        );
      }
      navigate('/enduser/requests/my-requests');
    } catch (e) {
      const body = e?.response?.data;
      if (body?.data?.formErrors || body?.data?.profileErrors) {
        setSubmitViolation({
          message: body.message || 'Could not submit',
          formErrors: body.data.formErrors || [],
          profileErrors: body.data.profileErrors || [],
        });
      }
      toast.error(body?.message || e?.message || 'Submission failed');
    } finally {
      setSubmitRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">Loading application draft…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert variant="warning" className="mt-3">
        {loadError}
        <div className="mt-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate(-1)}
          >
            Go back
          </Button>
        </div>
      </Alert>
    );
  }

  const liveOn = Boolean(serverRow?.liveProfileRefresh);

  return (
    <div className={`py-3 hcp-request-draft-module ${styles.sectionCard}`}>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-start flex-wrap gap-2 mb-3"
      >
        <div>
          <h4 className="mb-1">New request application</h4>
          <div className="text-muted small">
            {isDraftEditable
              ? 'Green highlight = still matches creation snapshot · Editable fields depend on policy · Autosaved.'
              : 'This draft was submitted · Read-only view.'}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          {saveState === 'saving' ? (
            <Badge bg="warning" text="dark" pill className={styles.savePill}>
              Saving…
            </Badge>
          ) : null}
          {saveState === 'saved' ? (
            <Badge bg="success" pill className={styles.savePill}>
              Saved
            </Badge>
          ) : null}
          {saveState === 'error' ? (
            <Badge bg="danger" pill className={styles.savePill}>
              Save failed
            </Badge>
          ) : null}
        </div>
      </Stack>

      <SubmissionReadinessAlert
        checking={submissionGate.checking}
        checked={submissionGate.checked}
        valid={submissionGate.valid}
        blockingMessage={submissionGate.blockingMessage}
        errors={submissionGate.errors}
      />

      {submitViolation?.message ? (
        <Alert
          variant="danger"
          className="mb-3"
          dismissible
          onClose={() => setSubmitViolation(null)}
        >
          <div className="fw-semibold mb-2">{submitViolation.message}</div>
          {submitViolation.formErrors?.length ? (
            <>
              <div className="small fw-semibold text-uppercase text-muted mb-1">
                Required fields
              </div>
              <ul className="small mb-2 mb-md-3">
                {submitViolation.formErrors.map((err, i) => (
                  <li key={`fe-${i}`}>
                    {err.field ? `${err.field}: ` : ''}
                    {err.detail || err.code}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {submitViolation.profileErrors?.length ? (
            <>
              <div className="small fw-semibold text-uppercase text-muted mb-1">
                Profile completeness
              </div>
              <ul className="small mb-0">
                {submitViolation.profileErrors.map((err, i) => (
                  <li key={`pe-${i}`}>
                    {err.field ? `${err.field}: ` : ''}
                    {err.detail || err.code}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Alert>
      ) : null}

      {isDraftEditable ? (
        <div className="text-muted small mb-2">
          Submit runs a final check (required fields + profile), saves your
          draft, then records it as submitted. Workflow processing will follow
          in a later release.
        </div>
      ) : null}

      {isDraftEditable && updateProfileAllowed ? (
        <Card className="mb-3 border-secondary-subtle">
          <Card.Body className="py-2 px-3">
            <Form.Check
              id="update-profile-after-submit"
              type="checkbox"
              label="Update my profile with these changes"
              checked={updateProfileAfterSubmit}
              disabled={submitRunning}
              onChange={(e) => setUpdateProfileAfterSubmit(e.target.checked)}
            />
            <small className="text-muted d-block mt-2">
              After a successful submission, editable values from this
              application are written to your HCP profile. Locked fields (for
              example verified ID or email, when restricted) are never changed.
              Updates are audited.
            </small>
          </Card.Body>
        </Card>
      ) : null}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          disabled={
            !isDraftEditable ||
            submitCheckRunning ||
            submissionGate.checking ||
            saveState === 'saving' ||
            submitRunning
          }
          onClick={handleVerifyForSubmission}
        >
          {submitCheckRunning ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2 align-middle"
              />
              Verifying…
            </>
          ) : (
            'Verify mandatory profile data'
          )}
        </Button>
        <Button
          variant="success"
          size="sm"
          disabled={
            !isDraftEditable ||
            submitRunning ||
            submitCheckRunning ||
            submissionGate.checking ||
            saveState === 'saving'
          }
          onClick={handleSubmitApplication}
        >
          {submitRunning ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2 align-middle"
              />
              Submitting…
            </>
          ) : (
            'Submit application'
          )}
        </Button>
        {!isDraftEditable ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate('/enduser/requests/my-requests')}
          >
            My requests
          </Button>
        ) : submissionGate.checked && submissionGate.valid ? (
          <span className="text-success small">
            Mandatory profile requirements are satisfied for this snapshot.
          </span>
        ) : null}
      </div>

      <Card className="mb-3 border-secondary-subtle">
        <Card.Body className="py-2 px-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <Form.Check
            type="switch"
            id="live-profile-refresh-toggle"
            label="Refresh populated data from profile on each load (live sync)"
            checked={liveOn}
            disabled={!isDraftEditable}
            onChange={(e) => handleLiveToggle(e.target.checked)}
          />
          <small className="text-muted">
            Turn off for a frozen snapshot until you resume editing saved draft.
          </small>
        </Card.Body>
      </Card>

      {draftMeta.length ? (
        <div className="small text-muted mb-3">
          {draftMeta.map(([k, v]) => (
            <span key={String(k)} className="me-3">
              <span className="fw-semibold text-body">{k}:</span> {String(v)}
            </span>
          ))}
        </div>
      ) : null}

      <ApplicantSection
        applicantInfo={populated.applicantInfo}
        baselineApplicant={highlightBaseline?.applicant}
        policy={policy}
        onFieldChange={patchApplicant}
        forceReadOnly={!isDraftEditable}
      />

      <LicenseSection
        licenseInfo={populated.licenseInfo}
        baselineLicense={highlightBaseline?.license}
        policy={policy}
        onFieldChange={patchLicense}
        forceReadOnly={!isDraftEditable}
      />

      <QualificationsSection
        education={populated.qualifications?.education}
        experience={populated.qualifications?.experience}
        baselineExperience={highlightBaseline?.experience}
        policy={policy}
        onExperienceChange={patchExperience}
        forceReadOnly={!isDraftEditable}
      />
    </div>
  );
}
